import { RequestError, redirectToLogin } from '@/requestErrorConfig';
import { request as appRequest } from '@/services/request';

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  code?: string;
  details?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ApiPage<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

interface ApiEnvelope<T> {
  code: number;
  msg: string;
  data: T;
  problem?: ProblemDetails;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, boolean | number | string | null | undefined>;
  body?: unknown;
  signal?: AbortSignal;
}

export class ApiProblemError extends Error {
  readonly problem: ProblemDetails | null;
  readonly status: number;

  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(problem?.detail ?? problem?.title ?? message);
    this.name = 'ApiProblemError';
    this.problem = problem ?? null;
    this.status = status;
  }
}

function isProblemDetails(value: unknown): value is ProblemDetails {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ProblemDetails>;
  return (
    typeof candidate.status === 'number' &&
    typeof candidate.title === 'string' &&
    typeof candidate.type === 'string'
  );
}

function isApiEnvelope<T>(value: unknown): value is ApiEnvelope<T> {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ApiEnvelope<T>>;
  return (
    typeof candidate.code === 'number' &&
    typeof candidate.msg === 'string' &&
    'data' in candidate
  );
}

export async function request<T>(
  path: string,
  { method = 'GET', query, body, signal }: RequestOptions = {},
): Promise<T> {
  try {
    const payload = await appRequest<ApiEnvelope<T> | undefined>(`/api${path}`, {
      method,
      params: query,
      data: body,
      signal,
      skipErrorHandler: true,
    });

    if (payload === undefined) return undefined as T;
    if (!isApiEnvelope<T>(payload)) {
      throw new ApiProblemError(500, 'Invalid Fake Server response');
    }
    if (payload.code !== 0) {
      const status = payload.code >= 400 && payload.code < 600 ? payload.code : 400;
      if (status === 401) redirectToLogin();
      throw new ApiProblemError(
        status,
        payload.msg,
        isProblemDetails(payload.problem)
          ? payload.problem
          : {
              type: 'about:blank',
              title: payload.msg,
              status,
              detail: payload.msg,
            },
      );
    }
    return payload.data;
  } catch (error) {
    if (error instanceof ApiProblemError) throw error;
    if (error instanceof RequestError && error.response) {
      const payload = await error.response
        .clone()
        .json()
        .catch(() => undefined);
      const problem = isProblemDetails(payload) ? payload : undefined;
      throw new ApiProblemError(
        error.response.status,
        error.response.statusText || 'Request failed',
        problem,
      );
    }
    throw error;
  }
}
